import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';

export interface ToastProps {
  id: string;
  message: string;
  variant: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: (id: string) => void;
}

export function ToastNotification({ id, message, variant, duration = 4000, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const percentage = (remaining / duration) * 100;
      setProgress(percentage);

      if (remaining > 0) {
        requestAnimationFrame(updateProgress);
      }
    };

    requestAnimationFrame(updateProgress);

    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 200);
  };

  const variantConfig = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-emerald-600',
      bg: 'bg-white',
      border: 'border-emerald-200',
      progressBar: 'bg-emerald-500',
    },
    error: {
      icon: XCircle,
      iconColor: 'text-red-600',
      bg: 'bg-white',
      border: 'border-red-200',
      progressBar: 'bg-red-500',
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-600',
      bg: 'bg-white',
      border: 'border-blue-200',
      progressBar: 'bg-blue-500',
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-amber-600',
      bg: 'bg-white',
      border: 'border-amber-200',
      progressBar: 'bg-amber-500',
    },
  };

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  return (
    <div
      className={`${config.bg} ${config.border} border-2 rounded-xl shadow-xl overflow-hidden min-w-[320px] max-w-md backdrop-blur-lg transition-all duration-200 ${
        isExiting ? 'animate-out fade-out slide-out-to-right-full' : 'animate-in fade-in slide-in-from-right-full'
      }`}
      role="alert"
      aria-live="polite"
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <IconComponent className="w-6 h-6" />
        </div>
        <p className="flex-1 text-gray-800 font-medium text-sm leading-relaxed pt-0.5">
          {message}
        </p>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors rounded-lg p-1 hover:bg-gray-100"
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="h-1 bg-gray-100">
        <div
          className={`h-full ${config.progressBar} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

interface ToastContainerProps {
  toasts: Array<{
    id: string;
    message: string;
    variant: 'success' | 'error' | 'info' | 'warning';
    duration?: number;
  }>;
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastNotification {...toast} onClose={onClose} />
        </div>
      ))}
    </div>
  );
}
